import React from 'react';
import { useSelector } from 'react-redux';

import type { LinkedImageList } from '@swf/cms';
import { CMSLinkedImageList } from '@swf/cms';
import { FeatureFlag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { Menu, SocialIcons } from '@swf/core/types/CMS';
import type { WidgetInstanceProps } from '@swf/core/types/Widget';
import type { FooterWidgetServices } from '@swf/generated/types/widgets/FooterWidget';

import styles from './FooterWidget.scss';
import FootNote from './components/FootNote/FootNote';
import FooterMenu from './components/FooterMenu/FooterMenu';
import Logos from './components/Logos/Logos';
import MachineDate from './components/MachineDate/MachineDate';
import SocialMediaIcons from './components/SocialMediaIcons/SocialMediaIcons';

export interface CMSData {
  footnote: string;
  logos: Menu;
  socialIcons: SocialIcons;
  socialMedia?: LinkedImageList[];
  menu: { label: string; items: Menu }[];
}

export type Props = WidgetInstanceProps<unknown, FooterWidgetServices, { cms: CMSData }>;

const FooterWidget: React.FC<Props> = ({ data }) => {
  const footerDateTimeFlag = useFeature(FeatureFlag.FooterDateTime);
  const isSportsSelfExcluded = useSelector(select.account.isSportsSelfExcluded);

  if (!data) {
    return null;
  }

  const { menu, logos, footnote, socialIcons, socialMedia } = data.cms;

  return (
    <footer className={styles.footer} role="contentinfo">
      {!!menu.length && !isSportsSelfExcluded && (
        <div className={styles.footerNavs}>
          {menu.map((subMenu, i) => (
            <FooterMenu key={i} {...subMenu} />
          ))}
        </div>
      )}
      {!!logos.length && <Logos logos={logos} />}
      {!!(footnote || socialIcons.length) && (
        <div className={styles.footerBottom}>
          <div className={styles.footerBottomInner}>
            {!!footnote && <FootNote footnote={footnote} />}
            {!!socialIcons.length && <SocialMediaIcons socialIcons={socialIcons} />}
            {!!socialMedia?.length && (
              <CMSLinkedImageList {...socialMedia[0]} isFooter type="Social Media" />
            )}
          </div>
        </div>
      )}
      {footerDateTimeFlag && <MachineDate />}
    </footer>
  );
};

export default FooterWidget;
